import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { hoursBetween } from "@/components/TimeRangePicker";

import { sydneyToday } from "@/lib/dates";
const EMPTY = { title: "", description: "", city: "", site_address: "", start_date: "", end_date: "", work_dates: [], flexible: false, duration_days: 1, day_rate: 600, required_tools: [], extra_tools: "", note: "", billing: "daily", hours: 4, hourly_rate: "", start_time: "", finish_time: "", weatherproof: false };

export function useInviteForm({ open, contractor, prefill, onCreated, onClose }) {
  const [tools, setTools] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF((s) => ({ ...s, ...prefill, day_rate: prefill?.day_rate || contractor?.day_rate || 500 }));
    api.get("/meta/tools").then(({ data }) => setTools(data.tools || [])).catch(() => {});
  }, [open, contractor, prefill]);

  const setK = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setDates = ({ dates, flexible, windowEnd }) => setF((s) => ({ ...s, work_dates: dates, flexible, start_date: dates[0] || "", end_date: flexible ? windowEnd : "", duration_days: flexible ? Math.max(1, Number(s.duration_days) || 1) : Math.max(1, dates.length) }));
  const toggleTool = (t) => setF((s) => ({ ...s, required_tools: s.required_tools.includes(t) ? s.required_tools.filter((x) => x !== t) : [...s.required_tools, t] }));
  const hourly = f.billing === "hourly";
  const total = hourly ? (Number(f.hours) * Number(f.hourly_rate) || 0) : (Number(f.day_rate) * Number(f.duration_days) || 0);
  const canSubmit = !busy && f.title && f.start_date && (f.flexible ? f.end_date : f.work_dates?.length > 0) && f.city && f.site_address?.trim() && f.start_time && f.finish_time && Number(f.hours) > 0 && (!hourly || (Number(f.hours) >= 4 && Number(f.hourly_rate) > 0));

  const [confirmOpen, setConfirmOpen] = useState(false);
  const submit = async (confirmed = false) => {
    if (confirmed !== true) { setConfirmOpen(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/requests", {
        contractor_id: contractor.id, title: f.title, description: f.description, city: f.city, site_address: f.site_address || null, site_lat: f.site_lat ?? null, site_lng: f.site_lng ?? null,
        start_date: new Date(f.start_date).toISOString(), end_date: f.end_date ? new Date(f.end_date).toISOString() : null,
        work_dates: hourly || f.flexible ? null : f.work_dates,
        flexible: !!f.flexible, duration_days: hourly ? 1 : Number(f.duration_days), day_rate: hourly ? null : Number(f.day_rate), required_tools: f.required_tools, extra_tools: f.extra_tools || null, note: f.note,
        billing: f.billing, hours: hourly ? Number(f.hours) : null, hourly_rate: hourly ? Number(f.hourly_rate) : null,
        start_time: f.start_time || null, finish_time: f.finish_time || null, weatherproof: !!f.weatherproof,
      });
      toast.success("Invitation sent");
      setConfirmOpen(false);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to send invite");
    } finally { setBusy(false); }
  };

  const setBilling = (billing, flex) => setF((s) => ({ ...s, billing, ...(billing === "hourly" && flex ? { start_time: flex.window_start || s.start_time, finish_time: flex.window_end || s.finish_time, hours: hoursBetween(flex.window_start, flex.window_end) || Math.max(4, Number(flex.hours_available) || 4), hourly_rate: flex.hourly_rate || Math.round((Number(s.day_rate) || 500) / 8), start_date: (flex.available_from || "").slice(0, 10) || s.start_date } : {}) }));
  const setTimes = (start_time, finish_time) => setF((s) => ({ ...s, start_time, finish_time, hours: hoursBetween(start_time, finish_time) }));
  return { f, setF, tools, busy, total, canSubmit, setK, setDates, toggleTool, submit, setBilling, setTimes, hourly, confirmOpen, setConfirmOpen, trade: contractor?.trade };
}
